import {
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
  Icon,
  Card,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from "@mui/material";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";
import MDInput from "theme/components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import axios from "axios.js";
import usePageTitle from "app/pageTitle";

import DataTable from "theme/Tables/DataTable";
import { useAwaitDialog } from "app/components/status_dialog";

export default function InventoryTrackersPage() {
  const title = "Inventory Trackers";
  usePageTitle(title);

  const navigate = useNavigate();

  const statusDialog = useAwaitDialog();
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [pageLoaded, setPageLoaded] = useState(false);
  const [refreshPage, setRefreshPage] = useState(false);
  const [trackers, setTrackers] = useState([]);

  const [trackerTitle, setTrackerTitle] = useState("");
  const [objectTypes, setObjectTypes] = useState([]);
  const [applyTo, setApplyTo] = useState("");
  const [objectKey, setObjectKey] = useState("");
  const [openTrackerDialog, setOpenTrackerDialog] = useState(false);

  const trackersTableColumns = [
    { Header: "Title", accessor: "title", align: "left" },
    { Header: "Type", accessor: "object_type", align: "left" },
    { Header: "Status", accessor: "status", align: "center" },
    { Header: "Actions", accessor: "actions", align: "right" },
  ];

  const buildRows = (data) => {
    const rows = [];

    data.map((row) => {
      rows.push({
        title: row.title,
        object_type: row.object_type,
        status: (
          <MDBox>
            <Icon color={row.status ? "success" : "error"}>
              {row.status ? "check_circle" : "cancel"}
            </Icon>
          </MDBox>
        ),
        actions: (
          <MDBox sx={{ width: "100%" }}>
            <MDBox mt={1} display="flex" gap={0.7} alignItems="center" justifyContent="flex-end">
              <Tooltip title="Manage Tracker">
                <MDButton
                  size="small"
                  variant="outlined"
                  color="dark"
                  iconOnly
                  onClick={() => navigate(`/inventory/trackers/${row.reference}`)}
                >
                  <Icon sx={{ cursor: "pointer" }} fontSize="small">
                    settings
                  </Icon>
                </MDButton>
              </Tooltip>
            </MDBox>
          </MDBox>
        ),
      });

      return null;
    });

    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);

      await axios
        .get("/portal/products/inventory/trackers.json")
        .then((result) => {
          setShowLoading(false);

          if (result.data.status === "success") {
            setTrackers(buildRows(result.data.data.trackers || []));
            setObjectTypes(result.data.data.object_types || []);
            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  const resetForm = () => {
    setTrackerTitle("");
    setApplyTo("");
    setObjectKey("");
  };

  const selectedObjectType = objectTypes.find((x) => x.key === applyTo);

  async function saveTracker() {
    if (!trackerTitle) {
      setSeverity("error");
      setErrMsg("Tracker title is required.");
      setOpen(true);
      return;
    }

    if (!applyTo) {
      setSeverity("error");
      setErrMsg("Please select what this tracker applies to.");
      setOpen(true);
      return;
    }

    if (!objectKey) {
      setSeverity("error");
      setErrMsg("Please select an item.");
      setOpen(true);
      return;
    }

    setOpenTrackerDialog(false);
    setShowLoading(true);

    const json = { title: trackerTitle, object_type: applyTo, object_key: objectKey };

    await axios
      .post("/portal/products/inventory/trackers/create.json", json)
      .then(async (result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          await statusDialog.show(result.data.data.message, "success", "Success!");

          resetForm();
          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <Dialog open={openTrackerDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Create Inventory Tracker</MDBox>
        </DialogTitle>

        <DialogContent>
          <MDBox>
            {/* <MDTypography
              variant="caption"
              color="text"
              fontWeight="bold"
              textTransform="uppercase"
            >
              General Details
            </MDTypography> */}

            <MDBox mt={1}>
              <MDInput
                label="Tracker Title"
                fullWidth
                value={trackerTitle}
                onChange={(e) => setTrackerTitle(e.target.value)}
              />
            </MDBox>

            <MDBox mt={3}>
              <MDInput
                select
                label="Apply To"
                fullWidth
                value={applyTo}
                onChange={(e) => {
                  setApplyTo(e.target.value);
                  setObjectKey("");
                }}
                SelectProps={{ style: { padding: "12px 0px" } }}
              >
                {objectTypes.map((row) => (
                  <MenuItem key={row.key} value={row.key}>
                    {row.name}
                  </MenuItem>
                ))}
              </MDInput>
            </MDBox>

            {selectedObjectType && (
              <MDBox mt={3}>
                <MDInput
                  select
                  label={`Select ${selectedObjectType.name}`}
                  fullWidth
                  value={objectKey}
                  onChange={(e) => setObjectKey(e.target.value)}
                  SelectProps={{ style: { padding: "12px 0px" } }}
                >
                  {selectedObjectType.options.map((option) => (
                    <MenuItem key={option.reference} value={option.reference}>
                      {option.title}
                    </MenuItem>
                  ))}
                </MDInput>
              </MDBox>
            )}
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenTrackerDialog(false);
              resetForm();
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>

          <MDButton color="dark" variant="contained" onClick={saveTracker}>
            Create
          </MDButton>
        </DialogActions>
      </Dialog>

      {pageLoaded && (
        <MDBox mb={5}>
          <MDBox mt={0} mb={1} display="flex" justifyContent="space-between">
            <MDBox>
              <Button
                color="dark"
                size="small"
                startIcon={<Icon>west</Icon>}
                onClick={() => navigate(`/home`)}
              >
                Back
              </Button>
            </MDBox>

            <MDBox>
              <Button color="dark" size="small" startIcon={<Icon>track_changes</Icon>}>
                Inventory Tracking
              </Button>
            </MDBox>
          </MDBox>

          <MDBox mt={2}>
            <Card sx={{ height: "100%", marginTop: "0px" }}>
              <MDBox p={2}>
                <MDBox align="center">
                  <MDBox>
                    <MDTypography variant="h6" fontSize={48}>
                      <Icon>track_changes</Icon>
                    </MDTypography>

                    <MDTypography mt={-0.5} variant="h6">
                      Physical Inventory Management
                    </MDTypography>

                    <MDTypography mt={1} variant="h6" fontWeight="light" fontSize={16}>
                      Track physical inventory given to your agents across sales, merchandising,
                      sampling and other product operations.
                      <br />
                      Monitor stock balances across your field teams.
                    </MDTypography>
                  </MDBox>

                  <MDBox mt={3} mb={3}>
                    <MDButton
                      variant="contained"
                      color="dark"
                      startIcon={<Icon>add</Icon>}
                      onClick={() => {
                        resetForm();
                        setOpenTrackerDialog(true);
                      }}
                    >
                      Create Inventory Tracker
                    </MDButton>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </MDBox>

          <MDBox mt={3}>
            <Card sx={{ height: "100%", marginTop: "0px" }}>
              <MDBox p={2}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                  <MDBox>
                    <MDTypography variant="h6" fontWeight="medium">
                      Inventory Trackers
                    </MDTypography>
                  </MDBox>

                  {/* <MDBox>
                    <MDButton variant="contained" color="dark" iconOnly size="small">
                      <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                    </MDButton>
                  </MDBox> */}
                </MDBox>
              </MDBox>

              <MDBox>
                <DataTable
                  table={{ columns: trackersTableColumns, rows: trackers }}
                  isSorted
                  entriesPerPage={false}
                  showTotalEntries
                  buttons
                  noEndBorder
                />
              </MDBox>
            </Card>
          </MDBox>
        </MDBox>
      )}

      <Footer />
    </DashboardLayout>
  );
}
